/* eslint-disable max-lines */
import _ from 'lodash'
import * as Diff from 'diff'

import { Set } from 'immutable'

import { CUSTOM_QUESTION_REG, SENTIMENTS } from '@aytm/app/stats/page/components/lib/constants'
import {
  ANSWER_VARIANTS_COMMENTABLE,
  CORRECTED_SPELLING,
  CORRECTIONS,
  ORIGINALS,
  DEFAULT_ORDER_BY,
  DEFAULT_ORDER_DIRECTION,
  DEFAULT_SELECTED_QUESTION,
  DEFAULT_PER_PAGE,
  DEFAULT_PER_PAGE_IMAGE_RESPONSES,
  DEFAULT_PER_PAGE_VIDEO_RESPONSES,
  HIDDEN_TAG_TYPES
} from '@aytm/app/stats/page/comments/lib/constants'

import { escapeRegExp } from '@aytm/lib/common/func'
import {
  getSurvey,
  questionIdToPosition,
  questionPositionToId,
  questionOldIdToId,
  getQuestionIcon,
  hasComments,
  removeTagsFromText,
  removeLogicFromText
} from '@aytm/app/stats/page/components/lib/utils'
import { collator, encodeText, decodeText, pluralize } from '@aytm/app/stats/lib/util/helpers'

export const getDottedQuestionName = (question = DEFAULT_SELECTED_QUESTION) =>
  (question != DEFAULT_SELECTED_QUESTION && question.replace(/(\d+)([a-z]+)/gi, '$1.$2')) || ''

export const getLastNodePath = (node = {}, path) => {
  const filtered = Object.keys(node).filter(itemKey => !['t', 'te', 'i', 'v', 'timings'].includes(itemKey))
  return filtered[filtered.length - 1]
    ? getLastNodePath(node[filtered[filtered.length - 1]], `${path}${filtered[filtered.length - 1]}`)
    : getDottedQuestionName(path)
}

export const questionByPath = (path, survey = {}, reportVirtual) => {
  const surveyObj = getSurvey(survey)
  const qn = path.split('.')[0]
  if (qn && CUSTOM_QUESTION_REG.test(qn)) return reportVirtual[qn]
  return surveyObj.questions[Number(qn.slice(1)) - 1]
}

export const questionByPosition = (visual_position, survey = {}) => {
  const surveyObj = getSurvey(survey)
  return surveyObj.questions.find(q => q.visual_position == visual_position)
}

// Q/PQ*position* + anything -> position
export const extractQuestionPosition = str => {
  const res = str.match(/^p?q(\d+)/i)
  return res && Array.isArray(res) ? Number(res[1]) : null
}

// q*position* + res of the path -> q/pq*visual_position* + rest of the path
export const pathQuestionIdToOld = (path, survey = {}) => {
  const subItems = getDottedQuestionName(path).split('.')
  if (!CUSTOM_QUESTION_REG.test(subItems[0])) subItems[0] = `q${questionIdToPosition(subItems[0], 1, survey)}`
  return subItems.join('.').replace('sq', 'b')
}

// q*position* + res of the path -> q/pq*visual_position* + rest of the path
export const pathQuestionOldIdToId = (path, survey = {}) => {
  const subItems = path.split('.')
  if (!CUSTOM_QUESTION_REG.test(subItems[0])) subItems[0] = questionOldIdToId(subItems[0], survey)
  return subItems.join('.')
}

export const responseTagsByPath = (response, key) => {
  const re = RegExp(`^${key}`)
  return (response.tags || []).filter(t => re.test(t.path))
}

export const cleanSearchData = (input = []) =>
  JSON.parse(JSON.stringify(input)).map(item => {
    // hm cluster
    if (null !== item.qid) {
      item.value =
        'string' == typeof item.value ? item.value.replace(new RegExp(`^${item.qid}__`, 'i'), '') : item.value || ''
      item.label =
        'string' == typeof item.label ? item.label.replace(new RegExp(`^${item.qid}: `, 'i'), '') : item.label || ''
    }

    // sentiment
    if ('sentiment' == item.type) {
      item.tag = item.value
      item.value = item.sentiment
      delete item.sentiment
      delete item.label
    }

    const ret = {
      type: item.type
    }

    if ('batch' != item.type) ret.value = item.value

    if (null !== item.qid) ret.qid = item.qid
    if (null !== item.path) ret.path = item.path
    if (null !== item.tag) ret.tag = item.tag
    if (null !== item.codebook) ret.codebook = item.codebook

    return ret
  })

export const mapReportSearchQuery = (input = []) =>
  input.map(item => {
    if (null !== item.qid && 'cluster' == item.type && !RegExp(`^${item.qid}__`, 'i').test(item.value)) {
      item.value = `${item.qid}__${item.label}`
      item.label = `${item.qid.toUpperCase()}: ${item.label}`
    }
    return item
  })

export const getToolBoxOptions = (
  survey = {},
  tagsList = []
  // eslint-disable-next-line max-params
) => {
  let result = []

  for (const codebook of survey.codeBooks || []) {
    const codebookTags = codebook.tags.reduce((acc, cur) => {
      const tag = tagsList.find(tag => `${codebook.title}: ${cur}` == tag.tag)
      if (tag && null !== tag) {
        return acc.concat(JSON.parse(JSON.stringify(tag)))
      }
      return acc
    }, [])

    if (0 < codebookTags.length) {
      result = [
        ...result,
        {
          label: codebook.title,
          options: codebookTags.sort((a, b) => collator.compare(a.label, b.label))
        }
      ]
    }
  }

  return result
}

// True only when the scope is provably gone: an empty report map or not-yet-arrived Xpert scripts
// cannot tell "removed" from "not loaded", so those answer false and the scope is kept.
export const isCustomScopeMissing = (selectedQuestion, reportVirtual, survey = {}) => {
  const m = String(selectedQuestion || '').match(CUSTOM_QUESTION_REG)
  if (!m) return false

  const id = `${m[1]}${m[2]}${m[3] ? `_${m[3]}` : ''}`.toLowerCase()
  const virtual = reportVirtual || {}
  const inVirtual = Object.hasOwn(virtual, id)

  if ('xq' == m[1].toLowerCase()) {
    const scripts = getSurvey(survey).scripts?.questions || {}
    return 0 < Object.keys(scripts).length && !Object.hasOwn(scripts, id) && !inVirtual
  }

  return 0 < Object.keys(virtual).length && !inVirtual
}

export const getSearchInputOptions = (
  query = [],
  survey = {},
  tagsList = [],
  questionOptions = [],
  selectedQuestion = null,
  isTagsManagementDisabled = true,
  isSentimentsManagementDisabled = true
  // eslint-disable-next-line max-params
) => {
  let result = []
  const filteredTagsList = tagsList.filter(tag => !query.some(item => 'tag' == item.type && item.value == tag.tag))

  if (!isSentimentsManagementDisabled) {
    result = [
      ...result,
      ...SENTIMENTS.map(({ value, label, type, sentiment }) => {
        return {
          type,
          value,
          label: `sentiment: ${label}`,
          sentiment
        }
      })
    ]
  }

  const question =
    null !== selectedQuestion && selectedQuestion != DEFAULT_SELECTED_QUESTION
      ? questionOptions.find(q => q.value == selectedQuestion) || null
      : null
  const position = (null !== question && extractQuestionPosition(selectedQuestion)) || null
  const isHMQuestion = null !== question && 'hm' == question.type && null !== position

  // get the list of heatmap batches
  if (isHMQuestion) {
    let visual_position = null
    try {
      visual_position = questionIdToPosition(selectedQuestion, 1, survey)
    } catch {
      // the scoped question has left the schema, so it has no batches to list
    }
    const questionObj = survey.questions?.find(item => item.position == visual_position)

    if (questionObj?.batches) {
      questionObj.batches.forEach(batch => {
        if (batch.comments) {
          const path = `q${position}.sq${batch.position}`
          if (!query.some(item => 'batch' == item.type && item.path == path)) {
            result.push({
              type: 'batch',
              value: `q${position}.sq${batch.position}: ${batch.text}`,
              label: removeLogicFromText(`Q${position}.SQ${batch.position}: ${batch.text}`),
              color: batch.color,
              path
            })
          }
        }
      })
    }
  }

  const surveyObj = getSurvey(survey)

  // get the list of heatmap clusters
  if (surveyObj.assets && surveyObj.assets['hm.cluster']) {
    surveyObj.assets['hm.cluster'].forEach(cluster => {
      if (
        (isHMQuestion && RegExp(`^${cluster.qid}([a-zA-Z]+|$)`).test(question.value)) ||
        (!question && questionOptions.some(q => RegExp(`^${cluster.qid}([a-zA-Z]+|$)`).test(q.value)))
      ) {
        const value = `${cluster.qid}__${decodeText(cluster.title, false)}`
        if (!query.some(item => 'cluster' == item.type && item.qid == cluster.qid && item.value == value)) {
          result.push({
            type: 'cluster',
            value,
            label: `${cluster.qid.toUpperCase()}: ${cluster.title}`,
            qid: cluster.qid
          })
        }
      }
    })
  }

  if (!isTagsManagementDisabled) {
    for (const codebook of survey.codeBooks || []) {
      const codebookTags = codebook.tags.reduce((acc, cur) => {
        const tag = filteredTagsList.find(tag => `${codebook.title}: ${cur}` == tag.tag)
        if (tag && null !== tag) {
          const _tag = JSON.parse(JSON.stringify(tag))
          _tag.type = 'tag'
          delete _tag.amount
          delete _tag.count
          delete _tag.countOnPage
          return acc.concat(_tag)
        }
        return acc
      }, [])

      if (0 < codebookTags.length) {
        const disabled = query.some(item => 'codebook' == item.type && item.value == codebook.title)

        result = [
          ...result,
          {
            type: 'codebook',
            value: disabled ? `${codebook.title} - disabled` : codebook.title,
            label: codebook.title,
            tags: codebook.tags,
            color: `#${codebook.color}`,
            disabled: disabled
          },
          ...codebookTags.sort((a, b) => collator.compare(a.label, b.label))
        ]
      }
    }
  }

  return result
}

export const getAnswerText = (prefix, text) => `<b>${prefix}:</b> ${text}`

export const isXpertOrVirtualType = q => {
  if (!q || null === q) return false

  let xpertOrVirtual = q.type && ['dlv', 'dlc', 'dlx', 'dld', 'dlt'].includes(q.type)
  if (!xpertOrVirtual) xpertOrVirtual = q.value && RegExp(/^(vq|xq)/i).test(q.value)
  return xpertOrVirtual
}
export const isVideoOrImageType = q => q.type && ['ir'].includes(q.type)

const hasCommentableAnswerVariants = (node = {}) => {
  if (node.comments) return true
  if (node.answerVariants) {
    return node.answerVariants.some(subNode => subNode && hasCommentableAnswerVariants(subNode))
  }
  return false
}

const hasSpecificCommentableAnswerVariants = (type, question, node = {}, survey = {}) => {
  const surveyObj = getSurvey(survey)
  if ('dlv' == type) {
    if (
      surveyObj.virtual &&
      surveyObj.virtual[question.id] &&
      surveyObj.virtual[question.id].answerVariants &&
      surveyObj.virtual[question.id].answerVariants[node.position - 1] &&
      surveyObj.virtual[question.id].answerVariants[node.position - 1].comments
    )
      return true
    return false
  }
  return true
}

export const getQuestionType = (question, survey = {}) => {
  const surveyObj = getSurvey(survey)
  if (null === question) return {}
  let type = question.type
  let chart = null
  if ('dlx' == type && surveyObj.scripts) {
    const questions = Object.keys(surveyObj.scripts.questions || {})
      .reduce((acc, cur) => {
        if (`${question.id}_` == surveyObj.scripts.questions[cur].id.slice(0, question.id.length + 1))
          acc.push(surveyObj.scripts.questions[cur])
        return acc
      }, [])
      .filter(v => v != undefined && null !== v)
    const iv = questions.find(q => q.blocks && q.blocks.find(b => 'chart' == b.type))
    if (iv != undefined) {
      chart = iv.blocks.find(b => 'chart' == b.type)
      type = chart.subtype
    }
  }
  return { type, chart }
}

export const getQuestionObject = (
  type,
  value,
  prefix,
  text = '',
  image = null,
  lvl,
  questionNumber,
  isFullQuestionName,
  isOptionHidden = false
  // eslint-disable-next-line max-params
) => {
  const ret = {
    type,
    text: getAnswerText(
      isFullQuestionName
        ? getDottedQuestionName(
            value.toUpperCase().includes(prefix)
              ? value.toUpperCase()
              : value.replace(/b(\d*)/g, `${prefix}`).toUpperCase()
          )
        : prefix,
      text
    ),
    value,
    prefix,
    image,
    lvl,
    questionText: text,
    questionNumber: isFullQuestionName ? value.toUpperCase() : questionNumber,
    isOptionHidden
  }
  return ret
}

export const getAnswerVariants = ({
  survey,
  question,
  answerVariants,
  selectedQuestion,
  position,
  prefix,
  text = '',
  questionNumber,
  lvl = 0,
  flag = false,
  isFullQuestionName = false,
  isOmitCheck = false
} = {}) => {
  const { image } = question
  const { type } = getQuestionType(question, survey)

  let result = []
  if (
    answerVariants &&
    answerVariants.some(
      av =>
        null !== av &&
        (isOmitCheck ||
          `${position}a${av.position}` == selectedQuestion ||
          (ANSWER_VARIANTS_COMMENTABLE.includes(type) &&
            hasSpecificCommentableAnswerVariants(type, question, av, survey)) ||
          hasCommentableAnswerVariants(av))
    )
  ) {
    if (!flag) {
      result = [
        ...result,
        getQuestionObject(type, position, prefix, text, image, lvl, questionNumber, isFullQuestionName)
      ]
    }
    answerVariants.forEach(av => {
      if (
        null !== av &&
        (isOmitCheck ||
          `${position}a${av.position}` == selectedQuestion ||
          (ANSWER_VARIANTS_COMMENTABLE.includes(type) &&
            hasSpecificCommentableAnswerVariants(type, question, av, survey)) ||
          hasCommentableAnswerVariants(av))
      ) {
        const isOptionHidden = 'hm' == type ? true : false
        result = [
          ...result,
          getQuestionObject(
            type,
            `${position}a${av.position}`,
            `A${av.position}`,
            av.text,
            av.image,
            lvl + 1,
            questionNumber,
            isFullQuestionName,
            isOptionHidden
          )
        ]
        if (av.answerVariants) {
          result = [
            ...result,
            ...getAnswerVariants({
              survey,
              question: Object.assign({}, question, { image: av.image }),
              answerVariants: av.answerVariants,
              selectedQuestion,
              position: `${position}a${av.position}`,
              prefix: `A${av.position}`,
              text: av.text,
              questionNumber,
              lvl: lvl + 1,
              flag: true,
              isFullQuestionName,
              isOmitCheck
            })
          ]
        }
      }
    })
  }
  return result
}

export const getBatches = (
  question = {},
  survey = {},
  selectedQuestion,
  text = '',
  isFullQuestionName,
  isOmitCheck
) => {
  let result = []
  let titleAdded = false
  const { id, position, image } = question
  const pos = id && CUSTOM_QUESTION_REG.test(String(id)) ? id : questionPositionToId(position, survey)
  const { type, chart } = getQuestionType(question, survey)
  const prefix = 'SQ'
  const batches = (chart || question).batches
  batches.forEach(batch => {
    const { answerVariants, comments } = batch
    if (answerVariants && (comments || answerVariants.some(item => item.comments))) {
      if (!titleAdded) {
        titleAdded = true
        result = [...result, getQuestionObject(type, pos, pos.toUpperCase(), text, image, 0, pos, isFullQuestionName)]
      }
      result = [
        ...result,
        ...getAnswerVariants({
          survey,
          question: Object.assign({}, question, { image: batch.image }),
          answerVariants,
          selectedQuestion,
          position: `${pos}sq${batch.position}`,
          prefix: `${prefix}${batch.position}`,
          text: batch.text,
          questionNumber: pos,
          lvl: 1,
          isFullQuestionName,
          isOmitCheck: comments || isOmitCheck
        })
      ]
    }
  })
  return result
}

export const getQuestionOptions = (
  question,
  survey = {},
  selectedQuestion,
  isFullQuestionName = false,
  isOmitCheck = false
) => {
  if (!question) return []

  const surveyObj = getSurvey(survey)
  const { id: questionID, position: questionPosition, image: questionImage = null, args: questionArgs } = question
  const pos = ['dlv', 'dlc', 'dlx', 'dld', 'dlt'].includes(question.type)
    ? questionID
    : questionPositionToId(questionPosition, survey)
  const { type, chart } = getQuestionType(question, surveyObj)
  const text = ('dlx' == question.type && questionArgs && questionArgs.header) || question.text || ''

  let result = []
  switch (type) {
    case 'ir':
    case 'va':
    case 'vr':
    case 'oe':
    case 'mn': {
      result = [getQuestionObject(type, pos, pos.toUpperCase(), text, questionImage, 0, pos, isFullQuestionName)]
      break
    }
    case 'cb':
    case 'rb':
    case 'scx':
    case 'dlv':
    case 'cx':
    case 'oex':
    case 'sh':
    case 'cn':
    case 'sft': {
      result = getAnswerVariants({
        survey: surveyObj,
        question,
        answerVariants: (chart || question).answerVariants,
        selectedQuestion,
        position: pos,
        prefix: pos.toUpperCase(),
        text,
        questionNumber: pos,
        isFullQuestionName,
        isOmitCheck
      })
      break
    }
    case 'mx': {
      if (chart) result = getBatches(question, surveyObj, selectedQuestion, text, isFullQuestionName, isOmitCheck)
      break
    }
    case 'mp':
    case 'hm': {
      result = getBatches(question, surveyObj, selectedQuestion, text, isFullQuestionName, isOmitCheck)
      break
    }
    default:
      break
  }
  return result
}

export const getQuestionsOptionsList = (
  survey = {},
  reportVirtual,
  reportSchema,
  selectedQuestion,
  specialMode = 'comments'
) => {
  const surveyObj = getSurvey(survey)
  let result = [{ text: 'All Questions', value: DEFAULT_SELECTED_QUESTION }]

  if (!surveyObj.questions) return result

  const questions = reportVirtual
    ? [...surveyObj.questions, ...Object.keys(reportVirtual).map(k => reportVirtual[k])]
    : surveyObj.questions

  if ('image-responses' == specialMode) {
    const question = questions.find(
      q => ['ir'].includes(q.type) && (!reportSchema.length || reportSchema.includes(`q${q.position}`))
    )

    if (question) {
      const options = getQuestionOptions(question, surveyObj, selectedQuestion, true)
      result = [...result, ...options]
    }
  }
  if ('video-responses' == specialMode) {
    const question = questions.find(
      q => ['va'].includes(q.type) && (!reportSchema.length || reportSchema.includes(`q${q.position}`))
    )

    if (question) {
      const options = getQuestionOptions(question, surveyObj, selectedQuestion, true)
      result = [{ ...options[0], value: DEFAULT_SELECTED_QUESTION }]
    }
  }
  if ('comments' == specialMode) {
    if (0 < reportSchema.length) {
      reportSchema.forEach(schemaKey => {
        const question = questions.find(
          q =>
            !['va', 'vr'].includes(q.type) && ((0 < q.position && `q${q.position}` == schemaKey) || q.id == schemaKey)
        )
        if (!question) return

        if (question) {
          const options = getQuestionOptions(question, surveyObj, selectedQuestion, true)
          result = [...result, ...options]
        }
      })
    } else {
      surveyObj.questions.forEach(question => {
        if (!question || ['va', 'vr'].includes(question.type)) return

        const options = getQuestionOptions(question, surveyObj, selectedQuestion, true)
        result = [...result, ...options]
      })
    }
  }

  return result
}

export const getGroupsList = survey => {
  let items = []
  const result = []

  const surveyObj = getSurvey(survey)
  if (!Object.hasOwn(surveyObj, 'questions')) return result

  if (Object.hasOwn(surveyObj, 'surveyLogic')) {
    for (const [key, question_group] of Object.entries(surveyObj.surveyLogic.question_groups || {})) {
      if (!/^gr\d+$/.test(key)) continue

      for (const group of question_group) {
        if (!group?.length) continue

        const questions = surveyObj.questions
          .filter(q => group.includes(`${(q.pre_type && 'pq') || 'q'}${q.visual_position}`))
          .filter(q => q != undefined && null !== q)
        // if (2 > questions.length || questions.some(q => !surveyObj.schema.includes(`q${q.position}`))) continue
        if (2 > questions.length) continue

        let prev_question = null
        const isNotSupported = questions.some((q, i) => {
          prev_question = 0 < i ? questions[i - 1] : null
          return prev_question
            ? q.type != prev_question.type ||
                q.subType != prev_question.subType ||
                (q.answerVariants != undefined &&
                  (prev_question.answerVariants == undefined ||
                    q.answerVariants.length != prev_question.answerVariants.length)) ||
                (q.batches != undefined &&
                  (prev_question.batches == undefined ||
                    q.batches[0].answerVariants?.length != prev_question.batches[0].answerVariants?.length)) ||
                false
            : false
        })
        if (isNotSupported) continue

        const icon = getQuestionIcon(questions[0])

        switch (questions[0].type) {
          case 'oex': {
            if (0 < questions[0].answerVariants?.length) {
              items = [
                ...items,
                {
                  icon,
                  text: questions.map(q => `Q${q.visual_position}`).join(', '),
                  value: questions.map(q => `q${q.visual_position}`).join(','),
                  type: questions[0].type,
                  lvl: 0
                },
                ...questions[0].answerVariants.map(av => {
                  return {
                    icon,
                    text: questions.map(q => `Q${q.visual_position}A${av.position}`).join(', '),
                    value: questions.map(q => `q${q.visual_position}a${av.position}`).join(','),
                    type: questions[0].type,
                    lvl: 0
                  }
                })
              ]
            }
            break
          }
          case 'cn': {
            if (0 < questions[0].answerVariants?.length) {
              items = [
                ...items,
                ...questions[0].answerVariants.map(av => {
                  return {
                    icon,
                    text: questions.map(q => `Q${q.visual_position}A${av.position}`).join(', '),
                    value: questions.map(q => `q${q.visual_position}a${av.position}`).join(','),
                    type: questions[0].type,
                    lvl: 0
                  }
                })
              ]
            }
            break
          }
          case 'mp':
          case 'hm': {
            if (
              questions[0].batches.some(b => 0 < b.answerVariants?.length && b.answerVariants.some(av => av.comments))
            ) {
              items = [
                ...items,
                ...questions[0].batches.map(b => {
                  return {
                    icon,
                    text: questions.map(q => `Q${q.visual_position}SQ${b.position}`).join(', '),
                    value: questions.map(q => `q${q.visual_position}sq${b.position}`).join(','),
                    type: questions[0].type,
                    lvl: 0
                  }
                })
              ]
            }
            break
          }
          case 'oe': {
            items = [
              ...items,
              {
                icon,
                text: questions.map(q => `Q${q.visual_position}`).join(', '),
                value: questions.map(q => `q${q.visual_position}`).join(','),
                type: questions[0].type,
                lvl: 0
              }
            ]
            break
          }
          case 'rb':
          case 'cb':
          case 'scx':
          case 'cx': {
            if (
              0 < questions[0].answerVariants?.length &&
              questions[0].answerVariants.some(av => null !== av && av.comments)
            ) {
              items = [
                ...items,
                {
                  icon,
                  text: questions.map(q => `Q${q.visual_position}`).join(', '),
                  value: questions.map(q => `q${q.visual_position}`).join(','),
                  type: questions[0].type,
                  lvl: 0
                }
              ]
            }
            break
          }
        }
      }
    }
  }

  return items
}
export const getQuestionsList = survey => {
  const result = []
  const surveyObj = getSurvey(survey)

  let items = getGroupsList(survey)
  if (0 < items.length) {
    result.push({
      text: 'Groups:',
      items
    })
  }

  items = []
  for (const question of surveyObj.questions) {
    if (['it', 'im', 'ir', 'va', 'vo', 'ed', 'vr'].includes(question.type)) continue

    const icon = getQuestionIcon(question)

    const options = getQuestionOptions(question, surveyObj, DEFAULT_SELECTED_QUESTION, true)
    items = [
      ...items,
      ...options
        .filter(option => !option.isOptionHidden)
        .map(option => {
          return {
            icon,
            text: `<b>${option.questionNumber}:</b> ${removeLogicFromText(option.questionText)}`,
            label: `<b>${option.questionNumber}:</b> ${removeLogicFromText(option.questionText)}`,
            search: `${option.questionNumber}: ${removeTagsFromText(removeLogicFromText(option.questionText))}`,
            value: option.value,
            lvl: option.lvl,
            disabled: 'cn' == option.type && option.prefix == option.questionNumber
          }
        })
    ]
  }

  if (0 < items.length) {
    result.push({
      text: 'Questions:',
      items
    })
  }

  return result
}

export const getFilteredQuestions = questions =>
  questions.filter(
    q =>
      ['oe', 'oex', 'va', 'cn', 'mn'].includes(q.type) ||
      (['cx', 'scx'].includes(q.type) && hasComments(q.answerVariants)) ||
      (['rb', 'cb'].includes(q.type) && hasComments(q.answerVariants)) ||
      ('mp' == q.type && hasComments(q.batches[0].answerVariants)) ||
      ('hm' == q.type && hasComments(q.batches))
  )

export const getSentimentsQuestions = questions =>
  questions.reduce((acc, cur) => {
    const res = {}
    const key = (0 < cur.position && 'q' + cur.position) || cur.id

    if (0 < cur.batches?.length) {
      if ('hm' == cur.type) {
        for (const b of cur.batches) {
          if (!b.answerVariants.some(av => av.comments)) continue

          res[`${key}.b${b.position}`] = {}
        }
      } else {
        for (const b of cur.batches) {
          const _res = {}
          const _key = `${key}.b${b.position}`

          for (const av of b.answerVariants) {
            if (!av.comments) continue

            _res[`${_key}.a${av.position}`] = {}
          }
          if (0 < Object.keys(_res).length) res[_key] = _res
        }
      }
      if (0 < Object.keys(res).length) acc[key] = res
    } else if ('mn' == cur.type) {
      acc[key] = {}
    } else if (0 < cur.answerVariants?.length && !['va'].includes(cur.type)) {
      const checkAnswerVariants = (k, answerVariants) => {
        let amount = 0

        for (const av of answerVariants) {
          if (null === av) continue

          const _key = `${k}.a${av.position}`
          const _amount = checkAnswerVariants(_key, av.answerVariants || [])

          if ('oex' == cur.type || av.comments) {
            res[_key] = {}
            amount++
          }
          amount += _amount
        }

        return amount
      }
      if (0 < checkAnswerVariants(key, cur.answerVariants)) acc[key] = res
    } else acc[key] = {}

    return acc
  }, {})

export const getDownloadUrl = (question, answer) => {
  if (['ir', 'va', 'vr'].includes(question.type)) {
    switch (question.type) {
      case 'ir':
        return answer.i && answer.i.original && answer.i.original.url
      case 'va':
      case 'vr':
      default:
        return answer.v && answer.v.mp4File
    }
  }
  return null
}

export const arrayToHash = (arr, defaultVal = 'true') => {
  return {
    ...arr.reduce((map, item) => {
      map[item] = defaultVal
      return map
    }, {})
  }
}

export const getSpellcheckerDiff = (text, fixedText) => {
  const diff = Diff.diffWords(text.replace(/<br\s*\/>/gi, '\n'), fixedText.replace(/<br\s*\/>/gi, '\n'))
  return diff.reduce((res, part) => {
    const [back2, back1] = res.slice(-2)
    if (!part.added && !part.removed && 0 == part.value.trim().length && 1 < res.length) {
      if (back2.removed && back1.added) {
        back1.value += part.value
        back2.value += part.value
      } else {
        res.push(part)
      }
    } else if (back2 && part.removed && back2.removed) {
      back2.value += part.value
    } else if (back2 && part.added && back2.added) {
      back2.value += part.value
    } else if (back1 && part.added && back1.added) {
      back1.value += part.value
    } else {
      res.push(part)
    }
    return res
  }, [])
}

export const highlightSearchString = (text, query) => {
  if (!query.length || !query.some(item => 'plain' == item.type)) return text
  let str = text
  let searchStringArr = []

  query.forEach(item => {
    if ('plain' == item.type) {
      searchStringArr = [...searchStringArr, item.value]
    }
  })
  for (const word of [...new Set(searchStringArr)]) {
    if ('@junk' == word.trim()) continue
    let strArray = str.split(/<em>/)
    strArray = strArray.flatMap(w => w.split(/<\/em>/))
    const re = RegExp(escapeRegExp(word.trim()), 'gim')
    str = strArray.map((w, i) => (0 < i % 2 && `<em>${w}</em>`) || w.replace(re, m => `<em>${m}</em>`)).join('')
  }
  return str
}

const getNestedSubquestions = subQuestionsList =>
  subQuestionsList.reduce((acc, currentValue) => {
    if (!subQuestionsList.some(item => new RegExp(`^${currentValue}(?!\\d+)[^\\s]+`, 'i').test(item))) {
      acc.push(currentValue)
    }
    return acc
  }, [])

const getSubQuestionsByPath = (path, questionsList, ret = []) => {
  if (questionsList.some(q => q.value == path)) {
    const subQuestions =
      path == DEFAULT_SELECTED_QUESTION
        ? questionsList.filter(q => q.value != DEFAULT_SELECTED_QUESTION)
        : questionsList.filter(q => new RegExp(`^${path}(?!\\d+)[^\\s]+`, 'i').test(q.value))

    if (subQuestions.length > 0) {
      subQuestions.forEach(sq => getSubQuestionsByPath(sq.value, questionsList, ret))
    }

    if (path && path != DEFAULT_SELECTED_QUESTION && !ret.includes(path)) {
      ret.push(path)
    }
  }
  return ret
}

export const quotedStringToAytmLogic = (str = '') => {
  // convert string with quotes & double-quotes to a corresponding aytm logic "STR STR+" expression
  const q = str.includes("'")
  const dq = str.includes('"')
  if (!q) {
    return `'${str}'`
  } else if (q && !dq) {
    return `"${str}"`
  } else {
    const cq = str.match(/'/g).length
    const cdq = str.match(/"/g).length
    const split = cq < cdq ? str.split("'").map(v => `'${v}'`) : str.split('"').map(v => `"${v}"`)
    return split
      .slice(0, -1)
      .reduce((a, v) => a.concat([v, cq < cdq ? `"'"` : `'"'`]), [])
      .concat(split.slice(-1))
      .join('')
  }
}

export const searchToFilterLogic = (
  query,
  selectedQuestion,
  questionsList = [],
  survey = {},
  separator = ' or ',
  subSeparator = ' or '
) => {
  const ret = []
  const logic = []

  const surveyObj = getSurvey(survey)

  let prefix = ''
  const question =
    selectedQuestion != DEFAULT_SELECTED_QUESTION ? questionsList.find(q => q.value == selectedQuestion) : null
  const { type: questionType } = getQuestionType(question, surveyObj)
  if ('cx' == questionType) {
    const parts = selectedQuestion.toUpperCase().split('A')
    if (1 < parts.length)
      prefix = parts
        .slice(1)
        .map((v, i) => `${parts[0]}R${i + 1}V = ${v}`)
        .join(' and ')
  }

  const searchStringArr = [...new Set(query.filter(item => 'plain' == item.type).map(item => item.value))]
  if (!searchStringArr.length) {
    searchStringArr.push('')
  }

  const addSearchStringArrToSubitems = (path, subitems) => {
    searchStringArr.forEach(str => {
      const subitem = 0 < str.trim().length ? `${path} like ${quotedStringToAytmLogic(str.trim())}` : `${path} != ''`
      if (!subitems.includes(subitem)) subitems.push(subitem)
    })
  }

  const subQuestionsList = getSubQuestionsByPath(selectedQuestion, questionsList)
  subQuestionsList.forEach(subQuestion => {
    const subitems = []
    const q = questionsList.find(q => q.value == subQuestion)
    const { type: qType } = getQuestionType(q, surveyObj)
    switch (qType) {
      case 'cx': {
        const p = subQuestion.toUpperCase().split('A')
        if (1 < p.length) {
          const path = `${p[0]}R${p.length - 1}C`
          if (surveyObj.refsList[path] != undefined) {
            addSearchStringArrToSubitems(path, subitems)
          }
        }
        break
      }
      default: {
        const reg = new RegExp(`^(X?Q)(\\d+)(?:SQ(\\d+))?(?:A(\\d+))?`, 'i')
        const p = subQuestion.toUpperCase().match(reg)
        if (null !== p) {
          const path =
            p[3] != undefined && p[4] != undefined
              ? `${p[1]}${p[2]}SQ${p[3]}A${p[4]}C`
              : p[4] != undefined
                ? `${p[1]}${p[2]}A${p[4]}C`
                : `${p[1]}${p[2]}C`
          if (surveyObj.refsList[path] != undefined) {
            addSearchStringArrToSubitems(path, subitems)
          }
        }
      }
    }
    if (separator != subSeparator)
      logic.push(1 < subitems.length ? `(${subitems.join(subSeparator)})` : subitems.join())
    else logic.push(...subitems)
  })

  const logicTextFiltered = logic.filter((val, id, array) => array.indexOf(val) == id)
  const logicText = logicTextFiltered.join(separator)
  if (logicText) {
    ret.push(
      `(${
        prefix.length
          ? 1 < logicTextFiltered.length
            ? `${prefix} and (${logicText})`
            : `${prefix} and ${logicText}`
          : logicText
      })`
    )
  }

  // response uid part
  const responseUidArr = [...new Set(query.filter(item => 'uid' == item.type).map(item => `uid=${item.value}`))]
  if (responseUidArr.length) {
    ret.push(`(${responseUidArr.join(separator)})`)
  }

  // Heatmap clusters part
  const clusterArr = []
  query
    .filter(item => 'cluster' == item.type)
    .forEach(item => {
      let qid = `${item.qid.toUpperCase()}`
      if (
        selectedQuestion != DEFAULT_SELECTED_QUESTION &&
        new RegExp(`^${qid}([a-zA-Z]+|$)`, 'i').test(selectedQuestion)
      ) {
        qid = selectedQuestion.toUpperCase().replace(new RegExp(`([0-9]+)B([0-9]+)`, 'i'), '$1SQ$2')
      }

      const clusterVal = item.label.replace(new RegExp(`^${item.qid}: `, 'i'), '')
      clusterArr.push(`${qid}R in cluster ${quotedStringToAytmLogic(clusterVal)}`)
    })

  if (clusterArr.length) {
    ret.push(`(${clusterArr.join(separator)})`)
  }

  // codebooks, tags and sentiment part
  const combine = (entityTypes = []) => {
    const _ret = []

    for (const entityType of entityTypes) {
      const entityArr = query
        .filter(item => entityType == item.type)
        .map(entity =>
          quotedStringToAytmLogic('tag' == entityType ? `${entity.codebook}: ${entity.value}` : `${entity.value}`)
        )

      if (0 < entityArr.length) {
        let qid
        if (question) {
          switch (question.type) {
            case 'cx': {
              const p = selectedQuestion.toUpperCase().split('A')
              // cascade Q3A1A2A3 -> Q3
              if (p.length) qid = p[0]
              break
            }
            default: {
              qid = selectedQuestion.toUpperCase().replace(new RegExp(`([0-9]+)B([0-9]+)`, 'i'), '$1SQ$2')
            }
          }
        }
        _ret.push(
          `${'codebook' == entityType ? 'codebook' : 'tag'}${'sentiment' == entityType ? '.SENTIMENT' : ''} ${
            qid || ''
          } ${1 < entityArr.length ? 'in' : '='} ${entityArr.join(',')}`
        )
      }
    }

    return 1 < _ret.length ? `(${_ret.join(' or ')})` : _ret.join(' or ')
  }
  ret.push(combine(['codebook', 'tag']))
  ret.push(combine(['sentiment']))

  return ret.filter(v => 0 < v.length).join(' and ')
}

export const getRejectsAndKudos = (cs = []) => {
  const kudos = {}
  const reject = {}
  cs.forEach(item => {
    if (item.extra && item.extra.kudos) {
      kudos[item.response.uid] = item.extra.kudos.revertable ? 'revertable' : 'true'
    }
    if (
      item.status &&
      ['open-ended-admin-rejected', 'open-ended-rejected', 'dc-auto-rejected', 'dc-rejected'].includes(item.status)
    ) {
      reject[item.response.uid] = 'true'
    }
  })
  return { kudos, reject }
}

export const getAnswerByPath = (answer, path) => path.split('.').reduce((o, i) => (o ? o[i] : {}), answer)

export const hasNestedAnswers = (answer, path) => {
  const answerByPath = getAnswerByPath(answer, pathQuestionIdToOld(path))
  return answerByPath != undefined && null !== answerByPath
    ? Object.keys(answerByPath).some(itemKey => !['t', 'te', 'i', 'v', 'timings'].includes(itemKey))
    : false
}

export const hasNestedQuestions = (questionOptions, nodeOptions, selectedQuestion) =>
  1 <
  questionOptions.filter(
    item =>
      !item.isOptionHidden &&
      item.questionNumber.startsWith(nodeOptions.questionNumber) &&
      (selectedQuestion === DEFAULT_SELECTED_QUESTION ||
        (selectedQuestion !== DEFAULT_SELECTED_QUESTION &&
          item.questionNumber.startsWith(selectedQuestion.toUpperCase())))
  ).length

// TODO !!!
// is given params == default params
export const getIsDefaultReportParams = (params = {}, specialType = 'comments') => {
  const {
    query = [],
    selectedQuestion = '',
    pageSize,
    orderBy = DEFAULT_ORDER_BY,
    orderDirection = DEFAULT_ORDER_DIRECTION,
    isShowAllTools,
    isShowAllTraits
  } = params
  return (
    !query.length &&
    (!selectedQuestion.length || selectedQuestion == DEFAULT_SELECTED_QUESTION) &&
    (!pageSize ||
      pageSize ===
        ('image-responses' == specialType
          ? DEFAULT_PER_PAGE_IMAGE_RESPONSES
          : 'video-responses' == specialType
            ? DEFAULT_PER_PAGE_VIDEO_RESPONSES
            : DEFAULT_PER_PAGE)) &&
    orderBy == DEFAULT_ORDER_BY &&
    orderDirection == DEFAULT_ORDER_DIRECTION &&
    isShowAllTools != true &&
    isShowAllTraits != true
  )
}

export const getDefaultData = (surveyData = {}) => {
  return {
    status: 'OK',
    totals: surveyData.totals || {},
    survey: surveyData.survey || {},
    cs: []
  }
}

export const showSpellcheckerDiff = (text, fixedText, correctionsLevel, style) => {
  switch (correctionsLevel) {
    case CORRECTIONS: {
      const diff = getSpellcheckerDiff(text, fixedText)
      return diff
        .reduce((result, part) => {
          const color =
            !(0 == part.value.trim().length && /[!,.]/.test(result[result.length - 1])) && part.added
              ? 'added'
              : part.removed
                ? 'removed'
                : null
          return result + `${color ? `<i class="${style[color]}">` : ''}${part.value}${color ? '</i>' : ''}`
        }, '')
        .replace(/\n/g, '<br/>')
    }
    case CORRECTED_SPELLING:
      return fixedText
    case ORIGINALS:
    default:
      return text
  }
}

export const checkSentimentStatus = (path = '', sentimentOptions = null) => {
  if (!path || !sentimentOptions) return true
  const pathArr = path.split('.')
  let res = true
  pathArr.reduce((acc, item) => {
    acc.push(item)
    if (false == sentimentOptions[acc.join('.')]) {
      res = false
    }
    return acc
  }, [])
  return res
}

export const getSentiment = (path, tags) => tags.find(t => 'sentiment' == t.type && path == t.path) || null

export const getNestedSentiment = (tags = [], path, node, sentimentOptions = null) => {
  const sentimentStatus = checkSentimentStatus(path, sentimentOptions)
  if (!sentimentStatus) return false
  if (tags.length) {
    const sentiment = getSentiment(path, tags)
    if (null !== sentiment) {
      return sentiment
    }
  }
  const filtered = !node.t
    ? Object.keys(node).find(itemKey => !['t', 'te', 'i', 'v', 'timings'].includes(itemKey))
    : null
  return filtered && null !== filtered
    ? getNestedSentiment(tags, `${path}.${filtered}`, node[filtered], sentimentOptions)
    : null
}

export const getLastItemPath = (questionsKeys, selectedQuestion, response) => {
  const lastAnswer = questionsKeys[questionsKeys.length - 1]
  if (!lastAnswer) return null
  return selectedQuestion != DEFAULT_SELECTED_QUESTION && !hasNestedAnswers(response.answer, selectedQuestion)
    ? getDottedQuestionName(pathQuestionIdToOld(selectedQuestion))
    : getLastNodePath(response.answer[lastAnswer?.keyName], pathQuestionIdToOld(lastAnswer?.path))
}

export const getLastSentimentValue = (lastItemPath, response) => {
  if (!response.tags || null === response.tags) return null
  const sentiments = response.tags.filter(tag => 'sentiment' == tag.type)
  if (!sentiments.length) return null

  return (sentiments.find(item => item.path == lastItemPath) || { tag: '' }).tag
}

export const getNodeCommentablePaths = (node = {}, tagType = 'manual', pathPrefix = '', list = []) => {
  let newList = list

  for (const key of Object.keys(node)) {
    if (CUSTOM_QUESTION_REG.test(key)) continue

    if (
      Object.keys(node[key]).some(item =>
        ('sentiment' == tagType ? ['t', 'te', 'v', 'timings'] : ['t', 'te', 'i', 'v', 'timings']).includes(item)
      )
    ) {
      newList.push(pathPrefix ? `${pathPrefix}.${key}` : key)
    }

    const filtered = Object.keys(node[key])
      .filter(ikey => !['t', 'te', 'i', 'v', 'timings'].includes(ikey))
      .reduce((obj, ikey) => {
        return {
          ...obj,
          [ikey]: node[key][ikey]
        }
      }, {})

    if (0 < Object.keys(filtered).length) {
      newList = getNodeCommentablePaths(filtered, tagType, `${pathPrefix ? `${pathPrefix}.` : ''}${key}`, newList)
    }
  }

  return newList
}

export const getCommentsCount = (data, tag, sentimentOptions = {}) => {
  const count = data.reduce((acc, item) => {
    const commentableNodes = getNodeCommentablePaths(item.answer, tag.type || 'manual').filter(node => {
      const path = node.split('.')
      return 'sentiment' != tag.type
        ? true
        : sentimentOptions
          ? !path.some((_, i) => false == sentimentOptions[path.slice(0, i + 1).join('.')])
          : true
    })
    // eslint-disable-next-line no-param-reassign
    return acc + commentableNodes.length
  }, 0)
  return Object.hasOwn(tag, 'countOnPage') ? (count >= tag.countOnPage ? count - tag.countOnPage : 0) : count
}

export const getTagOccurrence = (tag, type, data = [], selectedQuestion = null) =>
  data.reduce((count, item = {}) => {
    let path
    if (selectedQuestion && selectedQuestion != DEFAULT_SELECTED_QUESTION) {
      path = pathQuestionIdToOld(selectedQuestion)
    }
    const commentablePaths = getCommentsCount([item], { tag, type })
    if ('sentiment' == type && 'None' == tag) {
      // eslint-disable-next-line no-param-reassign
      count +=
        !item.tags || item.tags.filter(tagItem => 'sentiment' == tagItem.type).length < commentablePaths
          ? commentablePaths - (item.tags || []).filter(tagItem => 'sentiment' == tagItem.type).length
          : (item.tags || []).filter(
              tagItem =>
                tagItem.type == type &&
                tagItem.tag.toLowerCase() == tag.toLowerCase() &&
                (!path ? true : 0 == tagItem.path.lastIndexOf(path, 0))
            ).length
    } else {
      const commentableNodes = getNodeCommentablePaths(item.answer, type || 'manual')
      // eslint-disable-next-line no-param-reassign
      count += (commentableNodes || []).filter(node =>
        (item.tags || []).some(
          tagObj => tagObj.type == type && tagObj.tag.toLowerCase() == tag.toLowerCase() && tagObj.path == node
        )
      ).length
    }
    return count
  }, 0) || 0

const getTagsListObject = ({ tag, type, codeBooks, cs, count = 0, amount = 0, selectedQuestion, ret }) => {
  if (
    ret.some(
      item =>
        item.type == type &&
        (item.tag.toLowerCase() == tag.toLowerCase() || item.value.toLowerCase() == tag.toLowerCase())
    )
  ) {
    return null
  }

  const codebook = codeBooks.find(_codebook => _codebook.tags.map(_tag => `${_codebook.title}: ${_tag}`).includes(tag))

  return {
    tag: tag,
    type,
    label: codebook ? tag.replace(`${codebook.title}: `, '') : tag,
    value: tag,
    codebook: codebook?.title || null,
    color: `#${codebook?.color || '88d1cd'}`,
    amount,
    count,
    countOnPage: getTagOccurrence(tag, type, cs, selectedQuestion)
  }
}

export const getTagsList = (
  tags = {},
  {
    codeBooks = [],
    cs = [],
    tagsCounters = {},
    tagsStats = {},
    selectedQuestion = null,
    isSentimentsManagementDisabled = true
  } = {}
) => {
  let ret = []

  // tags from survey.tags
  Object.keys(tags).forEach(type => {
    if (['junk', 'sentiment', ...HIDDEN_TAG_TYPES].includes(type)) return

    tags[type].forEach(tag => {
      if (tagsCounters[type] && tagsCounters[type].some(item => item.tag == tag)) return

      const val = getTagsListObject({
        tag,
        type,
        codeBooks,
        cs,
        amount: Object.keys(tagsStats || {}).reduce(
          (acc, cur) => acc + (tagsStats[cur][type]?.find(item => item.tag == tag)?.amount || 0),
          0
        ),
        selectedQuestion,
        ret
      })
      if (val) ret.push(val)
    })
  })

  // tags from cs.tags
  if (tagsCounters) {
    Object.keys(tagsCounters).forEach(type => {
      if (['junk', 'sentiment', ...HIDDEN_TAG_TYPES].includes(type)) return

      tagsCounters[type].forEach(tag => {
        const val = getTagsListObject({
          tag: tag.tag,
          type,
          codeBooks,
          cs,
          amount: Object.keys(tagsStats || {}).reduce(
            (acc, cur) => acc + (tagsStats[cur][type]?.find(item => item.tag == tag.tag)?.amount || 0),
            0
          ),
          count: tag.amount,
          selectedQuestion,
          ret
        })
        if (val) ret.push(val)
      })
    })
  }

  // adding sentiments to the list
  if (!isSentimentsManagementDisabled) {
    ret = [
      ...ret,
      ...SENTIMENTS.map(({ value, sentiment }) => {
        return {
          tag: value,
          type: 'sentiment',
          label: value,
          value,
          color: '#414141',
          codebook: 'Sentiments',
          sentiment,
          amount: Object.keys(tagsStats || {}).reduce(
            (acc, cur) => acc + (tagsStats[cur]['sentiment']?.find(item => item.tag == value)?.amount || 0),
            0
          ),
          count: tagsCounters['sentiment']?.find(item => item.tag.toLowerCase() == value.toLowerCase())?.amount || 0,
          countOnPage: getTagOccurrence(value, 'sentiment', cs, selectedQuestion)
        }
      })
    ]
  }

  return ret
}

export const fixPathByQuestionType = (path, questionType) => {
  let ret = path
  if ('hm' == questionType) {
    ret = ret.split('.')
    ret.pop()
    ret = ret.join('.')
  }
  return ret
}

export const range = (start, end) => {
  if (start == end) return [start]
  return [start, ...range(start + 1, end)]
}

export const stripTags = (_html, ...args) => {
  const _tags = []
  let _tag = ''
  for (let _a = 1; _a < args.length; _a++) {
    _tag = args[_a].replace(/<|>/g, '').trim()
    if (args[_a].length > 0) _tags.push(_tag, '/' + _tag)
  }

  if (!('string' == typeof _html) && !(_html instanceof String)) return ''
  else if (0 == _tags.length) return _html.replace(/<(\s*\/?)[^>]+>/g, '')
  else {
    const _re = new RegExp('<(?!(' + _tags.join('|') + ')s*/?)[^>]+>', 'g')
    return _html.replace(_re, '')
  }
}

export const sanitizeReactSelectOption = value => {
  value.label = encodeText(value.label, false)
  value.value = encodeText(value.value, false)
  delete value.__isNew__
  return value
}

export const mergePageData = (pageData, cs = [], processedQA = []) => {
  const ret = pageData || []
  cs.forEach(item => {
    if (!ret.some(retItem => retItem.response.uid == item.response.uid) && !processedQA.includes(item.response.uid)) {
      ret.push(item)
    }
  })
  return ret
}

export const convertBasicToAdvanced = (query, joinOperator = 'or') => {
  const groupedObject = query.reduce((acc, item) => {
    acc[item.type] ||= []
    if (!acc[item.type].includes(item.value)) {
      acc[item.type].push('uid' != item.type ? `'${item.value}'` : item.value)
    }
    return acc
  }, {})

  const stringed = Object.keys(groupedObject).reduce((acc, key) => {
    if (groupedObject[key].length > 1) {
      acc.push(`${key} in ${groupedObject[key].join(', ')}`)
    } else {
      acc.push(`${key} ${'plain' == key ? '=' : '='} ${groupedObject[key]}`)
    }
    return acc
  }, [])

  return stringed.join(` ${joinOperator} `)
}

const getCodebooksPanelActionName = (codebook, action) => {
  switch (action) {
    case 'analysis': {
      return 'sentiment' == codebook.type ? 'Sentiment Analysis' : 'Codebook Analysis'
    }
    case 'delete': {
      return 'removal'
    }
    default:
      return action
  }
}

const getTagsPanelActionName = action => {
  switch (action) {
    case 'assign': {
      return 'assignment'
    }
    case 'delete':
    case 'remove_page':
    case 'remove_found': {
      return 'removal'
    }
    default:
      return action
  }
}

export const getCodebooksSentimentItemOptionsList = ({
  codebook,
  selectedAction,
  tagsList,
  isSentimentsManagementDisabled,
  isAiAssistantsOECodingDisabled,
  onActionCancel,
  onActionConfirm,
  onActionClick
}) => {
  let ret = []

  if (selectedAction) {
    ret = [
      {
        label: `Confirm ${getCodebooksPanelActionName(codebook, selectedAction)}`,
        onClick: () => onActionConfirm()
      },
      {
        label: 'Cancel',
        onClick: () => onActionCancel()
      }
    ]
  } else if (!isSentimentsManagementDisabled && !isAiAssistantsOECodingDisabled) {
    ret.push({
      label: `${
        tagsList.some(tag => 'sentiment' == tag.type && 'None' != tag.tag && 0 < tag.amount) ? 'Rerun' : 'Run'
      } Sentiment Analysis`,
      onClick: () => onActionClick('analysis')
    })
  }

  return ret
}
export const getCodebooksItemOptionsList = ({
  codebook,
  selectedQuestion,
  selectedAction,
  selectedActionStatus,
  isSkipperDisabled,
  isAiAssistantsOECodingDisabled,
  onActionCancel,
  onActionConfirm,
  onActionClick
}) => {
  let ret = []

  if (selectedAction) {
    ret = [
      {
        label: `Confirm ${getCodebooksPanelActionName(codebook, selectedAction)}`,
        onClick: () => onActionConfirm()
      },
      {
        label: 'Cancel',
        onClick: () => onActionCancel()
      }
    ]
  } else {
    ret.push({ label: 'Rename the codebook', onClick: () => onActionClick('update') })
    ret.push({
      label: 'Clone codebook',
      onClick: () => onActionClick('clone')
    })

    if (0 < codebook.tags.length) {
      ret.push({
        label: 'Copy codebook structure',
        status: 'copied' == selectedActionStatus ? 'Copied!' : null,
        onClick: () => onActionClick('copy')
      })
    }

    if (!isSkipperDisabled && !isAiAssistantsOECodingDisabled) {
      if (0 < codebook.tags.length) {
        ret.push({
          icon: 'icon-aytmfd-skipper',
          label: `Apply codebook${
            selectedQuestion != DEFAULT_SELECTED_QUESTION ? ` to ${selectedQuestion.toUpperCase()}` : ''
          }`,
          onClick: () => onActionClick('skipper-apply')
        })
      }
      ret.push({
        icon: 'icon-aytmfd-skipper',
        label: `Run guided autocode${
          selectedQuestion != DEFAULT_SELECTED_QUESTION ? ` on ${selectedQuestion.toUpperCase()}` : ''
        }`,
        onClick: () => onActionClick('skipper-guided')
      })
    }

    ret.push({
      icon: 'icon-aytmfd-trash',
      label: 0 < codebook.tags.length ? 'Delete codebook and codes from all reports' : 'Delete codebook',
      onClick: () => onActionClick('delete')
    })
  }

  return ret
}

export const getTagsSentimentItemOptionsList = ({
  specialMode = 'comments',
  tag,
  selectedAction,
  isSentimentsDisabled,
  onActionCancel,
  onActionConfirm,
  onActionClick
}) => {
  let ret = []

  if (selectedAction) {
    ret = [
      {
        label: `Confirm ${getTagsPanelActionName(selectedAction)}`,
        onClick: () => onActionConfirm()
      },
      {
        label: 'Cancel',
        onClick: () => onActionCancel()
      }
    ]
  } else if (!isSentimentsDisabled) {
    if ('None' != tag.tag) {
      if (0 < tag.countOnPage) {
        ret.push({
          label: `Remove sentiment from <b>${tag.countOnPage}</b> ${pluralize(tag.countOnPage || 0, 'comment', 'comments')} on page`,
          onClick: () => onActionClick('remove_page')
        })
      }

      if (0 < tag.amount && tag.countOnPage < tag.amount) {
        ret.push({
          label: `Remove sentiment from all <b>${tag.amount}</b> ${pluralize(tag.amount, 'found comment', 'found comments')}`,
          onClick: () => onActionClick('remove_found')
        })
      }
    }

    if ('charts' != specialMode) {
      ret.push({ label: 'Assign to multiple comments', onClick: () => onActionClick('assign') })
    }
  }

  return ret
}
export const getTagsItemOptionsList = ({
  specialMode = 'comments',
  tag,
  selectedAction,
  onActionCancel,
  onActionConfirm,
  onActionClick
}) => {
  let ret = []

  if (selectedAction) {
    ret = [
      {
        label: `Confirm ${getTagsPanelActionName(selectedAction)}`,
        onClick: () => onActionConfirm()
      },
      {
        label: 'Cancel',
        onClick: () => onActionCancel()
      }
    ]
  } else {
    if (0 < tag.countOnPage) {
      ret.push({
        label: `Remove code from <b>${tag.countOnPage}</b> ${pluralize(tag.countOnPage || 0, 'comment', 'comments')} on page`,
        onClick: () => onActionClick('remove_page')
      })
    }

    if (0 < tag.amount && tag.countOnPage < tag.amount) {
      ret.push({
        label: `Remove code from all <b>${tag.amount}</b> ${pluralize(tag.amount, 'found comment', 'found comments')}`,
        onClick: () => onActionClick('remove_found')
      })
    }

    ret.push({ label: 'Rename the code', onClick: () => onActionClick('update') })

    if ('charts' != specialMode) {
      ret.push({ label: 'Assign to multiple comments', onClick: () => onActionClick('assign') })
    }

    ret.push({
      icon: 'icon-aytmfd-trash',
      label: 'Delete code itself from all reports',
      onClick: () => onActionClick('delete')
    })
  }

  return ret
}

export const hasVirtualOrXpert = (questionsList = [], selectedQuestion) => {
  let isVirtualOrXpertInScope = questionsList.some(q => isXpertOrVirtualType(q))
  let isOnlyVirtualOrXpert = !questionsList.some(q => q.type && !isXpertOrVirtualType(q))
  if (isVirtualOrXpertInScope && selectedQuestion && selectedQuestion != DEFAULT_SELECTED_QUESTION) {
    const question =
      selectedQuestion != DEFAULT_SELECTED_QUESTION ? questionsList.find(q => q.value == selectedQuestion) : null
    if (isXpertOrVirtualType(question)) {
      isOnlyVirtualOrXpert = true
    } else {
      isVirtualOrXpertInScope = false
    }
  }
  return [isVirtualOrXpertInScope, isOnlyVirtualOrXpert]
}

export const hasDisabledSentimentInScope = (questionsList = [], sentimentOptions, selectedQuestion = '') => {
  const isDisabledSentimentInScope = sentimentOptions
    ? getSubQuestionsByPath(selectedQuestion, questionsList).some(q =>
        sentimentOptions && q != DEFAULT_SELECTED_QUESTION
          ? false == checkSentimentStatus(pathQuestionIdToOld(q), sentimentOptions)
          : false
      )
    : false

  let isOnlyDisabledSentiment = false
  if (isDisabledSentimentInScope) {
    if (selectedQuestion && selectedQuestion != DEFAULT_SELECTED_QUESTION) {
      isOnlyDisabledSentiment = false == checkSentimentStatus(pathQuestionIdToOld(selectedQuestion), sentimentOptions)
      if (!isOnlyDisabledSentiment) {
        const nestedSQs = getNestedSubquestions(getSubQuestionsByPath(selectedQuestion, questionsList))
        isOnlyDisabledSentiment = !nestedSQs.some(sq =>
          checkSentimentStatus(pathQuestionIdToOld(sq.replace(/sq/i, 'b')), sentimentOptions)
        )
      }
    } else {
      isOnlyDisabledSentiment = !questionsList.some(q => {
        if (q.value == DEFAULT_SELECTED_QUESTION) return false
        return checkSentimentStatus(pathQuestionIdToOld(q.value), sentimentOptions)
      })
    }
  }

  return [isDisabledSentimentInScope, isOnlyDisabledSentiment]
}
